/**
 * Empty cart overlay when header cart opens with no last-known sales point.
 * Shop CartSheet is not mounted on Purchases hub / platform chrome.
 * Top-anchored under shell header end icons — same chrome panel as Profile / Settings
 * (Org · Cart · Settings · Profile).
 */

import { useEffect, useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../shared/ui/Button.js';
import { useOptionalOverlayBackDismiss } from '../shared/context/OverlayBackDismissContext.js';
import { CustomerChromeTopPanel } from './CustomerChromeTopPanel.js';

export interface HubEmptyCartSheetProps {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly onBrowseShops: () => void;
}

export function HubEmptyCartSheet({
  open,
  onClose,
  onBrowseShops,
}: HubEmptyCartSheetProps): JSX.Element {
  const { t } = useTranslation('customer');
  const titleId = useId();
  const descriptionId = useId();
  const overlayBack = useOptionalOverlayBackDismiss();
  const registerOverlayDismiss = overlayBack?.registerOverlayDismiss;

  useEffect(() => {
    if (!open || registerOverlayDismiss === undefined) {
      return;
    }
    return registerOverlayDismiss(onClose);
  }, [open, onClose, registerOverlayDismiss]);

  return (
    <CustomerChromeTopPanel
      open={open}
      onClose={onClose}
      title={t('shop.cartSheet.title')}
      titleId={titleId}
      describedBy={descriptionId}
      closeLabel={t('shop.cartSheet.close.aria')}
      testId="customer-cart-sheet"
      contentAlign="center"
      className="bg-[var(--color-surface-elevated)]"
    >
      <div className="flex flex-col items-center gap-5 text-center">
        <p
          id={descriptionId}
          className="w-full text-sm text-[var(--color-on-surface-muted)]"
          data-testid="customer-hub-empty-cart-copy"
        >
          {t('shop.cart.hubEmpty')}
        </p>
        <Button
          intent="primary"
          size="lg"
          block
          onClick={onBrowseShops}
          data-testid="customer-hub-empty-cart-browse"
        >
          {t('shop.cart.hubEmptyBrowse')}
        </Button>
      </div>
    </CustomerChromeTopPanel>
  );
}
